import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Layers,
  ChevronDown,
  MapPin,
  Briefcase,
  Users,
  CheckCircle2,
  AlertTriangle,
  Info,
  Settings,
  Plus,
  Pencil,
  Trash2,
  Power,
  BarChart3,
  CalendarCheck,
  CalendarDays,
  ArrowRight,
  Building2,
  Shield,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

const FAQ_DATA = [
  {
    q: "What is Company Structure in BrixiGo?",
    a: "Company Structure is where you define and manage your organisation's Branches (office locations), Departments (functional groups like HR, Finance), and Designations (job titles like Manager, Executive). These are used across the system when creating employees, filtering reports, and tracking attendance.",
  },
  {
    q: "Where do I manage Company Structure?",
    a: "Go to Settings → Organisation → Company Structure. This page has three tabs — Branches, Departments, and Designations. You can also access individual sections directly via Settings → Organisation → Branches / Departments / Designations.",
  },
  {
    q: "Who can access Company Structure settings?",
    a: "The Company Structure page (combined view) is available to Admin users only. The individual section pages (Branches, Departments, Designations) are accessible to both Admin and HR roles.",
  },
  {
    q: "What is the difference between a Branch and a Location?",
    a: "In BrixiGo, Branch and Location refer to the same entity. The settings page labels them as 'Branches', while the employee form uses 'Location'. Both use the same data — a branch you create in settings will appear as a location option when creating an employee.",
  },
  {
    q: "How do I add a new Department?",
    a: "Go to Settings → Organisation → Company Structure → Departments tab. Click '+ Add Department', enter the Department Name (required) and an optional Description, then click Save.",
  },
  {
    q: "How do I add a new Branch?",
    a: "Go to Settings → Organisation → Company Structure → Branches tab. Click '+ Add Branch', enter the Branch Name (required), and optionally select a State and enter a City, then click Save.",
  },
  {
    q: "How do I add a new Designation?",
    a: "Go to Settings → Organisation → Company Structure → Designations tab. Click '+ Add Designation', enter the Designation Name (required) and an optional Description, then click Save.",
  },
  {
    q: "Can I add a Department while creating an employee?",
    a: "Yes. On the Employee Create form (Step 2 — Organisation), each dropdown for Department, Designation, and Location has a '+ Add' option. You can type a name and save it inline without leaving the employee form.",
  },
  {
    q: "What are the auto-generated codes (BR001, DPT001, DESG001)?",
    a: "BrixiGo automatically generates display codes based on position — BR001, BR002 for branches, DPT001, DPT002 for departments, and DESG001, DESG002 for designations. These are display-only labels shown in the table and are not editable.",
  },
  {
    q: "Can I deactivate a Department, Branch, or Designation instead of deleting it?",
    a: "Yes. Each row in the table has a toggle (power icon) action button. Clicking it switches the item between Active and Inactive status. Inactive items remain in the system but may not appear in dropdown selections.",
  },
  {
    q: "What happens if I delete a Department that has employees?",
    a: "The delete action will attempt to remove the record. If employees are currently assigned to it, the backend may prevent deletion. You will see an error message if the delete fails. Consider deactivating instead of deleting if the item is in use.",
  },
  {
    q: "Why is my Department not appearing in the Employee form?",
    a: "Ensure the department has been created in Settings → Organisation → Departments and is marked as Active. If you just created it, try refreshing the Employee form. Also confirm you have the correct role (Admin or HR) to view departments.",
  },
  {
    q: "Where do Departments and Designations appear in reports?",
    a: "Department and Designation are available as filter options in Employee Reports (Master, Directory, Joining), Attendance Reports, Leave Reports, Payroll Reports, ESI Reports, and TDS Reports. They also appear as columns in report tables.",
  },
];

const ERROR_SOLUTIONS = [
  {
    error: "Branch name is required",
    cause: "You tried to save a branch without entering a name.",
    fix: "Enter a valid name in the Branch Name field before clicking Save.",
  },
  {
    error: "Department name is required",
    cause: "You tried to save a department without entering a name.",
    fix: "Enter a valid name in the Department Name field before clicking Save.",
  },
  {
    error: "Designation name is required",
    cause: "You tried to save a designation without entering a name.",
    fix: "Enter a valid name in the Designation Name field before clicking Save.",
  },
  {
    error: "Failed to save branch",
    cause: "The branch could not be saved due to a server error or network issue.",
    fix: "Check your internet connection and try again. If the error persists, the branch name may already exist.",
  },
  {
    error: "Failed to save department",
    cause: "The department could not be saved due to a server error or network issue.",
    fix: "Check your internet connection and try again. If the error persists, the department name may already exist.",
  },
  {
    error: "Failed to save designation",
    cause: "The designation could not be saved due to a server error or network issue.",
    fix: "Check your internet connection and try again. If the error persists, the designation name may already exist.",
  },
  {
    error: "Failed to load branches / departments / designations",
    cause: "The system could not retrieve the list from the server.",
    fix: "Refresh the page. If the issue continues, check your network connection or contact support.",
  },
  {
    error: "Failed to update status",
    cause: "The Active/Inactive toggle could not be saved.",
    fix: "Refresh the page and try toggling the status again. Ensure you have Admin or HR permissions.",
  },
  {
    error: "Failed to delete",
    cause: "The item could not be deleted, possibly because it is assigned to employees.",
    fix: "Check if any employees are currently assigned to this item. Reassign them first, then retry the delete. Alternatively, deactivate the item instead of deleting it.",
  },
  {
    error: "Please select a department / designation / location",
    cause: "The employee form requires all three organisational fields but one or more are empty.",
    fix: "Select a value from each dropdown. If no options are available, use the '+ Add' button in the dropdown to create one, or go to Settings → Organisation to set them up.",
  },
  {
    error: "Failed to load departments, designations, or locations",
    cause: "The employee form could not fetch the dropdown options from the server.",
    fix: "Refresh the page. Ensure the items have been created in Settings → Organisation. If the issue persists, check your network connection.",
  },
  {
    error: "Could not save. Try again.",
    cause: "The inline quick-add on the employee form failed to create the item.",
    fix: "Try adding the item from Settings → Organisation instead. If the error includes specific details, follow those instructions.",
  },
];

export default function CompanyStructureHelpPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);

  const filteredFaqs = search.trim()
    ? FAQ_DATA.filter(
        (f) =>
          f.q.toLowerCase().includes(search.toLowerCase()) ||
          f.a.toLowerCase().includes(search.toLowerCase())
      )
    : FAQ_DATA;

  const filteredErrors = search.trim()
    ? ERROR_SOLUTIONS.filter(
        (e) =>
          e.error.toLowerCase().includes(search.toLowerCase()) ||
          e.cause.toLowerCase().includes(search.toLowerCase()) ||
          e.fix.toLowerCase().includes(search.toLowerCase())
      )
    : ERROR_SOLUTIONS;

  return (
    <div className="brix-page">
      {/* Hero */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-emerald">
              <Layers size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Company Structure</h1>
              <p className="hs-hero-sub">
                Manage your organisation's branches, departments, and designations in BrixiGo.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search — e.g. branch, department, designation..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* ── Overview ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Company Structure Overview</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Company Structure in BrixiGo lets you define the organisational building blocks
            used across the system. It consists of three entities:
          </p>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <MapPin size={18} className="ic-emerald" />
              <span><strong>Branches</strong> — Office locations (also called Locations)</span>
            </div>
            <div className="ah-feature-item">
              <Briefcase size={18} className="ic-violet" />
              <span><strong>Departments</strong> — Functional groups (e.g. HR, Finance, Engineering)</span>
            </div>
            <div className="ah-feature-item">
              <Users size={18} className="ic-amber" />
              <span><strong>Designations</strong> — Job titles (e.g. Manager, Executive, Analyst)</span>
            </div>
          </div>
          <p>
            These entities are required when creating employees and are used for filtering
            in reports, attendance, and leave modules. You manage them from
            <strong> Settings → Organisation → Company Structure</strong>.
          </p>
        </div>
      </section>

      {/* ── Where to Configure ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Settings size={20} className="ah-section-icon ic-slate" />
          <h2 className="hs-section-title">Where to Configure</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Company Structure can be accessed in two ways:
          </p>
          <div className="ah-workflow-steps">
            <div className="ah-workflow-step">
              <div className="ah-step-num">1</div>
              <div className="ah-step-body">
                <strong>Combined View</strong>
                <span>
                  Settings → Organisation → Company Structure — a tabbed page with
                  Branches, Departments, and Designations in one place.
                  <em> Admin only.</em>
                </span>
              </div>
            </div>
            <div className="ah-workflow-step">
              <div className="ah-step-num">2</div>
              <div className="ah-step-body">
                <strong>Individual Sections</strong>
                <span>
                  Settings → Organisation → Branches / Departments / Designations —
                  each section can be accessed individually.
                  <em> Admin and HR roles.</em>
                </span>
              </div>
            </div>
          </div>

          <div className="ah-info-box">
            <Info size={16} className="ic-blue" />
            <span>
              The Company Structure page is part of the Organisation Setup flow. Its navigation
              links to Financial Year (previous) and Employee Setup (next) to guide you
              through initial configuration.
            </span>
          </div>
        </div>
      </section>

      {/* ── Access Control ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Shield size={20} className="ah-section-icon ic-indigo" />
          <h2 className="hs-section-title">Access Control</h2>
        </div>
        <div className="ah-content-card">
          <table className="ah-manage-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Allowed Roles</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Company Structure (combined)</td>
                <td><span className="ah-status-badge ic-blue">Admin</span></td>
              </tr>
              <tr>
                <td>Branches (individual)</td>
                <td>
                  <span className="ah-status-badge ic-blue">Admin</span>{" "}
                  <span className="ah-status-badge ic-violet">HR</span>
                </td>
              </tr>
              <tr>
                <td>Departments (individual)</td>
                <td>
                  <span className="ah-status-badge ic-blue">Admin</span>{" "}
                  <span className="ah-status-badge ic-violet">HR</span>
                </td>
              </tr>
              <tr>
                <td>Designations (individual)</td>
                <td>
                  <span className="ah-status-badge ic-blue">Admin</span>{" "}
                  <span className="ah-status-badge ic-violet">HR</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Branches ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <MapPin size={20} className="ah-section-icon ic-emerald" />
          <h2 className="hs-section-title">Branches</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Branches represent your organisation's office locations. They are labelled as
            "Branches" in settings and as "Location" in the employee form — both refer to
            the same data.
          </p>

          <h3 className="ah-subsection-title">Branch Fields</h3>
          <table className="ah-manage-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Branch Name</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>Name of the office location (e.g. "Head Office - Mumbai")</td>
              </tr>
              <tr>
                <td>State</td>
                <td>—</td>
                <td>Dropdown of Indian states and territories (32 options)</td>
              </tr>
              <tr>
                <td>City</td>
                <td>—</td>
                <td>City name (e.g. "Mumbai")</td>
              </tr>
            </tbody>
          </table>

          <h3 className="ah-subsection-title">Branch Table Columns</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <span><strong>Branch Code</strong> — Auto-generated (BR001, BR002, ...)</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Branch Name</strong> — The name you entered</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>State</strong> — Selected state, or "---" if not set</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Status</strong> — Active or Inactive badge</span>
            </div>
          </div>

          <h3 className="ah-subsection-title">Actions</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <Plus size={16} className="ic-green" />
              <span><strong>Add Branch</strong> — Opens a drawer form to create a new branch</span>
            </div>
            <div className="ah-feature-item">
              <Pencil size={16} className="ic-blue" />
              <span><strong>Edit</strong> — Opens the drawer with pre-filled values</span>
            </div>
            <div className="ah-feature-item">
              <Trash2 size={16} className="ic-rose" />
              <span><strong>Delete</strong> — Shows confirmation: "Delete [name]? This cannot be undone."</span>
            </div>
            <div className="ah-feature-item">
              <Power size={16} className="ic-amber" />
              <span><strong>Toggle Status</strong> — Switch between Active and Inactive</span>
            </div>
          </div>

          <div className="ah-info-box">
            <Info size={16} className="ic-blue" />
            <span>
              Branch and Location are the same entity in BrixiGo. The API endpoint
              is <code>/setup/locations</code> for both. When you create a branch in
              settings, it appears as a "Location" option in the employee form.
            </span>
          </div>
        </div>
      </section>

      {/* ── Departments ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Briefcase size={20} className="ah-section-icon ic-violet" />
          <h2 className="hs-section-title">Departments</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Departments represent functional groups within your organisation (e.g. Human
            Resources, Finance, Engineering). They are the most widely used company structure
            entity — available as a filter in almost every module.
          </p>

          <h3 className="ah-subsection-title">Department Fields</h3>
          <table className="ah-manage-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Department Name</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>Name of the department (e.g. "Human Resources")</td>
              </tr>
              <tr>
                <td>Description</td>
                <td>—</td>
                <td>Optional description of the department</td>
              </tr>
            </tbody>
          </table>

          <h3 className="ah-subsection-title">Department Table Columns</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <span><strong>Department Code</strong> — From server or auto-generated (DPT001, DPT002, ...)</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Department Name</strong> — The name you entered</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Status</strong> — Active or Inactive badge</span>
            </div>
          </div>

          <h3 className="ah-subsection-title">Actions</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <Plus size={16} className="ic-green" />
              <span><strong>Add Department</strong> — Opens a drawer form</span>
            </div>
            <div className="ah-feature-item">
              <Pencil size={16} className="ic-blue" />
              <span><strong>Edit</strong> — Opens the drawer with current values</span>
            </div>
            <div className="ah-feature-item">
              <Trash2 size={16} className="ic-rose" />
              <span><strong>Delete</strong> — Confirmation: "Delete [name]? This cannot be undone."</span>
            </div>
            <div className="ah-feature-item">
              <Power size={16} className="ic-amber" />
              <span><strong>Toggle Status</strong> — Switch between Active and Inactive</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Designations ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Users size={20} className="ah-section-icon ic-amber" />
          <h2 className="hs-section-title">Designations</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Designations represent job titles within your organisation (e.g. Chief Executive
            Officer, Manager, Analyst). They are assigned to employees during creation and
            displayed in employee lists and reports.
          </p>

          <h3 className="ah-subsection-title">Designation Fields</h3>
          <table className="ah-manage-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Designation Name</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>Job title (e.g. "Chief Executive Officer")</td>
              </tr>
              <tr>
                <td>Description</td>
                <td>—</td>
                <td>Optional description of the designation</td>
              </tr>
            </tbody>
          </table>

          <h3 className="ah-subsection-title">Designation Table Columns</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <span><strong>Designation Code</strong> — Auto-generated (DESG001, DESG002, ...)</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Designation Name</strong> — The name you entered</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Status</strong> — Active or Inactive badge</span>
            </div>
          </div>

          <h3 className="ah-subsection-title">Actions</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <Plus size={16} className="ic-green" />
              <span><strong>Add Designation</strong> — Opens a drawer form</span>
            </div>
            <div className="ah-feature-item">
              <Pencil size={16} className="ic-blue" />
              <span><strong>Edit</strong> — Opens the drawer with current values</span>
            </div>
            <div className="ah-feature-item">
              <Trash2 size={16} className="ic-rose" />
              <span><strong>Delete</strong> — Confirmation: "Delete [name]? This cannot be undone."</span>
            </div>
            <div className="ah-feature-item">
              <Power size={16} className="ic-amber" />
              <span><strong>Toggle Status</strong> — Switch between Active and Inactive</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Auto-Generated Codes ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-teal" />
          <h2 className="hs-section-title">Auto-Generated Codes</h2>
        </div>
        <div className="ah-content-card">
          <p>
            BrixiGo automatically generates display codes for each entity based on its
            position in the list. These codes are for display purposes only and are not
            editable.
          </p>
          <table className="ah-manage-table">
            <thead>
              <tr>
                <th>Entity</th>
                <th>Prefix</th>
                <th>Example Codes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Branches</td>
                <td><code>BR</code></td>
                <td>BR001, BR002, BR003</td>
              </tr>
              <tr>
                <td>Departments</td>
                <td><code>DPT</code></td>
                <td>DPT001, DPT002, DPT003</td>
              </tr>
              <tr>
                <td>Designations</td>
                <td><code>DESG</code></td>
                <td>DESG001, DESG002, DESG003</td>
              </tr>
            </tbody>
          </table>
          <div className="ah-info-box">
            <Info size={16} className="ic-blue" />
            <span>
              For Departments, the code may come from the server if one was assigned.
              Otherwise, it falls back to the auto-generated format (DPT001, etc.).
            </span>
          </div>
        </div>
      </section>

      {/* ── How Company Structure Relates to Employees ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Users size={20} className="ah-section-icon ic-violet" />
          <h2 className="hs-section-title">How Company Structure Relates to Employees</h2>
        </div>
        <div className="ah-content-card">
          <p>
            When creating an employee (Step 2 — Organisation), three company structure fields
            are required under the "Team & Placement" section:
          </p>
          <table className="ah-manage-table">
            <thead>
              <tr>
                <th>Employee Form Field</th>
                <th>Required</th>
                <th>Source</th>
                <th>Validation Message</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Department</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>Settings → Departments</td>
                <td>"Please select a department"</td>
              </tr>
              <tr>
                <td>Designation</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>Settings → Designations</td>
                <td>"Please select a designation"</td>
              </tr>
              <tr>
                <td>Location</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>Settings → Branches</td>
                <td>"Please select a location"</td>
              </tr>
            </tbody>
          </table>

          <h3 className="ah-subsection-title">Quick-Add from Employee Form</h3>
          <p>
            If no options exist for a dropdown, the form shows:
            "No [label] yet — add one below or complete Initial Setup." Each dropdown
            includes a <strong>+ Add</strong> option that lets you create a new Department,
            Designation, or Location inline without leaving the employee form. After saving,
            the new item is automatically selected.
          </p>

          <h3 className="ah-subsection-title">Where Structure Appears in Employee Views</h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <span><strong>Employee List</strong> — Department and Designation columns displayed; searchable by department and designation</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Employee Preview</strong> — Department, Designation, and Location shown in the detail drawer</span>
            </div>
          </div>

          <div className="ah-info-box">
            <Info size={16} className="ic-blue" />
            <span>
              For detailed employee management documentation, see the
              <strong> Employee Module</strong> help page.
            </span>
          </div>
        </div>
      </section>

      {/* ── Where Company Structure Is Used ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <BarChart3 size={20} className="ah-section-icon ic-sky" />
          <h2 className="hs-section-title">Where Company Structure Is Used</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Company structure entities appear as filters and columns across multiple modules.
            Below is where each entity is verified to be used:
          </p>

          <h3 className="ah-subsection-title">
            <BarChart3 size={16} className="ic-sky" /> Reports
          </h3>
          <table className="ah-manage-table">
            <thead>
              <tr>
                <th>Report Module</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Location / Branch</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Employee Reports (Master, Directory, Joining)</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>—</td>
              </tr>
              <tr>
                <td>Payroll Reports (Register, Salary, LOP)</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Attendance Reports (Daily, Monthly, Absent, Late, Register)</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Leave Reports (Balance, Utilization, Pending)</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>ESI Reports</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
              </tr>
              <tr>
                <td>TDS Reports</td>
                <td><CheckCircle2 size={14} className="ic-green" /></td>
                <td>—</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>

          <h3 className="ah-subsection-title">
            <CalendarCheck size={16} className="ic-green" /> Attendance
          </h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <span><strong>Daily Attendance</strong> — Department filter dropdown ("All departments")</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Weekly Admin View</strong> — Department filter and department column per employee</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Department Month Calendar</strong> — Department dropdown to filter the calendar view</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Employee Attendance View</strong> — Shows employee's department and designation</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Shift Assignments</strong> — Department used when assigning shifts</span>
            </div>
          </div>

          <h3 className="ah-subsection-title">
            <CalendarDays size={16} className="ic-teal" /> Leave
          </h3>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <span><strong>Leave Balances</strong> — Department and Designation filter dropdowns with active filter chips</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Leave Approval</strong> — Department name shown per leave request</span>
            </div>
            <div className="ah-feature-item">
              <span><strong>Leave Dashboard</strong> — Department referenced in analytics</span>
            </div>
          </div>

          <div className="ah-info-box">
            <Info size={16} className="ic-blue" />
            <span>
              The Payroll module itself does not directly use company structure fields.
              However, Payroll <strong>Reports</strong> (Register, Salary Register, LOP Report)
              include Department as a filter option.
            </span>
          </div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section className="ah-section" ref={faqRef}>
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Frequently Asked Questions</h2>
        </div>

        {filteredFaqs.length === 0 && (
          <div className="ah-content-card" style={{ textAlign: "center", color: "var(--brix-slate-400)" }}>
            No FAQs match your search.
          </div>
        )}

        <div className="hs-faq-list">
          {filteredFaqs.map((faq, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i} className={`hs-faq-item ${isOpen ? "open" : ""}`}>
                <button
                  type="button"
                  className="hs-faq-q"
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                  aria-expanded={isOpen}
                >
                  <span>{faq.q}</span>
                  <ChevronDown size={16} className={`hs-faq-chevron ${isOpen ? "rotated" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      className="hs-faq-a"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <p>{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Error Solutions ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <AlertTriangle size={20} className="ah-section-icon ic-rose" />
          <h2 className="hs-section-title">Common Issues & Solutions</h2>
        </div>

        {filteredErrors.length === 0 && (
          <div className="ah-content-card" style={{ textAlign: "center", color: "var(--brix-slate-400)" }}>
            No issues match your search.
          </div>
        )}

        {filteredErrors.map((err, i) => (
          <div key={i} className="ah-error-card">
            <div className="ah-error-title">
              <AlertTriangle size={14} className="ic-rose" />
              <code>{err.error}</code>
            </div>
            <div className="ah-error-body">
              <p><strong>Why it happens:</strong> {err.cause}</p>
              <p><strong>How to fix:</strong> {err.fix}</p>
            </div>
          </div>
        ))}
      </section>

      {/* ── Related Help ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <ArrowRight size={20} className="ah-section-icon ic-slate" />
          <h2 className="hs-section-title">Related Help</h2>
        </div>
        <div className="ah-content-card">
          <div className="ah-feature-grid">
            <div
              className="ah-feature-item"
              style={{ cursor: "pointer" }}
              role="link"
              tabIndex={0}
              onClick={() => navigate("/help/employees")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/help/employees")}
            >
              <Users size={18} className="ic-violet" />
              <span><strong>Employee Module</strong> — Managing employee records and organisation details</span>
            </div>
            <div
              className="ah-feature-item"
              style={{ cursor: "pointer" }}
              role="link"
              tabIndex={0}
              onClick={() => navigate("/help/settings")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/help/settings")}
            >
              <Settings size={18} className="ic-slate" />
              <span><strong>Settings Guide</strong> — System settings including organisation setup</span>
            </div>
            <div
              className="ah-feature-item"
              style={{ cursor: "pointer" }}
              role="link"
              tabIndex={0}
              onClick={() => navigate("/help/reports")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/help/reports")}
            >
              <BarChart3 size={18} className="ic-sky" />
              <span><strong>Reports</strong> — Using department and designation filters in reports</span>
            </div>
            <div
              className="ah-feature-item"
              style={{ cursor: "pointer" }}
              role="link"
              tabIndex={0}
              onClick={() => navigate("/help/getting-started")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/help/getting-started")}
            >
              <Building2 size={18} className="ic-blue" />
              <span><strong>Getting Started</strong> — Initial setup including organisation configuration</span>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
